import React, { useState } from 'react';
import { Share2, Copy, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ShareExercisesButton = ({ workoutId, clientId, clientName, hasActiveShareLink = false, className = "", onShareDeleted, onShareCreated }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [shareData, setShareData] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleShare = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`/api/workouts/${workoutId}/clients/${clientId}/share-exercises`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('trainer_token')}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Ошибка при создании ссылки:', response.status, errorData);
                throw new Error(`Ошибка при создании ссылки: ${response.status}`);
            }

            const data = await response.json();
            setShareData(data);
            setShowModal(true);
            // Отправляем событие для обновления данных на других страницах
            localStorage.setItem('share_link_updated', Date.now().toString());
            // Вызываем callback для обновления состояния на родительской странице
            if (onShareCreated) {
                onShareCreated();
            }
        } catch (error) {
            console.error('Ошибка при создании ссылки:', error);
            toast.error('Не удалось создать ссылку для шаринга');
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareData.share_url);
            setCopied(true);
            toast.success('Ссылка скопирована в буфер обмена');
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Ошибка при копировании:', error);
            toast.error('Не удалось скопировать ссылку');
        }
    };

    const handleDeleteShare = async () => {
        setIsDeleting(true);
        try {
            const response = await fetch(`/api/workouts/${workoutId}/clients/${clientId}/share-exercises`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('trainer_token')}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Ошибка при удалении ссылки:', response.status, errorData);
                throw new Error(`Ошибка при удалении ссылки: ${response.status}`);
            }

            toast.success('Ссылка успешно удалена');
            setShowModal(false);
            setShareData(null);
            // Отправляем событие для обновления данных на других страницах
            localStorage.setItem('share_link_updated', Date.now().toString());
            // Вызываем callback для обновления состояния на родительской странице
            if (onShareDeleted) {
                onShareDeleted();
            }
        } catch (error) {
            console.error('Ошибка при удалении ссылки:', error);
            toast.error('Не удалось удалить ссылку');
        } finally {
            setIsDeleting(false);
        }
    };

    const formatExpiryDate = (dateString, timezone = 'Europe/Moscow') => {
        const date = new Date(dateString);
        
        // Форматируем дату в часовом поясе тренера
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone
        });
    };

    return (
        <>
            <button
                onClick={handleShare}
                disabled={isLoading}
                className={`${className} relative disabled:opacity-50 disabled:cursor-not-allowed`}
                title={`Поделиться упражнениями для ${clientName}`}
            >
                {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                    <>
                        <Share2 size={20} />
                        {hasActiveShareLink && (
                            <span className="absolute top-1 left-1 w-2 h-2 bg-green-500 rounded-full border-2 border-white shadow" style={{zIndex:2}} />
                        )}
                    </>
                )}
            </button>

            {/* Модальное окно с ссылкой */}
            {showModal && shareData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Поделиться упражнениями
                            </h3>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                                Ссылка для просмотра упражнений клиента <strong>{clientName}</strong>:
                            </p>
                            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
                                <input
                                    type="text"
                                    value={shareData.share_url}
                                    readOnly
                                    className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
                                />
                                <button
                                    onClick={copyToClipboard}
                                    className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                                    title="Копировать ссылку"
                                >
                                    {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>

                        <div className="text-xs text-gray-500 mb-4">
                            <p>Ссылка действительна до: {formatExpiryDate(shareData.expires_at, shareData.trainer_timezone)}</p>
                            <p className="mt-1">Ссылка будет автоматически деактивирована через 2 недели</p>
                        </div>

                        <div className="flex justify-between gap-2">
                            {hasActiveShareLink && (
                                <button
                                    onClick={handleDeleteShare}
                                    disabled={isDeleting}
                                    className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isDeleting ? 'Удаление...' : 'Удалить ссылку'}
                                </button>
                            )}
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 ml-auto"
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ShareExercisesButton; 